import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { Api as AirchefApi } from './airchef/Api';
import { Api as PimApi } from './pim/Api';
import { loginRequest, msalInstance } from '@/components/auth/authConfig';
import { MissingClaimsError, MissingTokenError, NoActiveAccountError } from '@/common/errors';
import { useBoundStore } from '@/stores/root';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { logApi } from './apihelper';
import { FeatureFlags } from '@/common/FeatureFlags';

const airchefApiUrl = import.meta.env.VITE_AIRCHEF_API_URL ?? 'http://localhost:8084';
const pimApiUrl = import.meta.env.VITE_PIM_API_URL ?? 'http://localhost:8085';

async function idTokenInterceptor(config: InternalAxiosRequestConfig<unknown>) {
  let account = msalInstance.getActiveAccount();

  if (!account) {
    const foundAccount = msalInstance.getAccount({
      tenantId: import.meta.env.VITE_AZURE_TENANT_ID,
    });

    if (foundAccount) {
      msalInstance.setActiveAccount(foundAccount);
    }

    account = msalInstance.getActiveAccount();
    if (!account) {
      await msalInstance.loginRedirect();
      throw new NoActiveAccountError();
    }
  }

  try {
    const idTokenClaims = account.idTokenClaims as { exp: number } | undefined;
    if (!idTokenClaims) {
      throw new MissingClaimsError('No id token claims in account info');
    }
    const forceRefresh = new Date(idTokenClaims.exp * 1000) < new Date();
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh,
    });

    if (!tokenResponse.idToken) {
      throw new MissingTokenError('Missing id token in token response');
    }

    config.headers['Authorization'] = `Bearer ${tokenResponse.idToken}`;
  } catch (err) {
    if (err instanceof Error) {
      console.error(err);
      if (
        err instanceof InteractionRequiredAuthError ||
        err instanceof MissingClaimsError ||
        err instanceof MissingTokenError
      ) {
        await msalInstance.loginRedirect(loginRequest);
      }
    }
  }

  return config;
}

async function versionInterceptor(response: AxiosResponse) {
  let ver = typeof response.headers?.get === 'function' ? response.headers.get('x-build-version') : 'unknown';

  const service = response.config.baseURL?.includes(airchefApiUrl) ? 'airchef' : 'pim';

  ver = Array.isArray(ver) ? ver[0] : ver;
  ver = typeof ver === 'string' ? ver : JSON.stringify(ver);

  useBoundStore.getState().planner.setServiceVersion(service, ver);

  return response;
}
function addInterceptors(client: AxiosInstance) {
  client.interceptors.request.use(idTokenInterceptor);
  client.interceptors.response.use(versionInterceptor);

  if (FeatureFlags.logRequests) {
    client.interceptors.request.use((request) => {
      const isPim = request.baseURL?.includes(pimApiUrl) ?? false;
      logApi(isPim ? 'pim' : 'airchef', request.url ?? '', request.method);
      return request;
    });

    client.interceptors.response.use(
      (response) => {
        const isPim = response.config.baseURL?.includes(pimApiUrl) ?? false;
        logApi(isPim ? 'pim' : 'airchef', `${response.config.url}`, response.config.method, true, response.status);
        return response;
      },
      (error) => {
        if (error instanceof AxiosError) {
          const isPim = error?.config?.baseURL?.includes(pimApiUrl) ?? false;
          logApi(
            isPim ? 'pim' : 'airchef',
            `${error?.response?.config.url}`,
            error?.response?.config.method,
            true,
            error?.response?.status,
          );
        }

        return Promise.reject(error);
      },
    );
  }
}

export const airchefApi = new AirchefApi({
  baseURL: airchefApiUrl,
});

export const pimApi = new PimApi({
  baseURL: pimApiUrl,
});

addInterceptors(airchefApi.instance);
addInterceptors(pimApi.instance);
